import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Resolve legacy `aivue/…` object-storage paths to full backend URLs.
 * Bunny thumbnail/HLS URLs already start with https:// so pass through.
 */
function resolveMediaUrl(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const base = (API || '').replace(/\/+$/, '');
  return `${base}/api/files/${url.replace(/^\/+/, '')}`;
}

const formatShort = (seconds) => {
  const s = Math.max(0, Math.floor(seconds || 0));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return m > 0 ? `${m}:${String(rem).padStart(2, '0')}` : `${rem}s`;
};

/**
 * LiveBanner — dark themed card at the top of Home advertising the 24/7
 * channel. Tapping goes to /live. Renders nothing when the channel is
 * empty (spec: no card at all if there are no playable videos).
 *
 * The banner:
 *   • Fetches /api/channel/now ONCE on mount + every 60 s (cheap — the
 *     endpoint is 5-min-cached server-side anyway).
 *   • Ticks its own 1-second local timer so the progress bar advances
 *     smoothly between server hits — no visible drift because we re-anchor
 *     on every fetch.
 *   • Rolls over locally when `remaining` hits 0 by nudging up the fetch
 *     cadence, so a viewer sitting on Home for a while doesn't see the
 *     progress bar visibly stall at 100 % waiting for the next refresh.
 */
export const LiveBanner = () => {
  const navigate = useNavigate();
  const [nowData, setNowData] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef(null);
  const fetchTimerRef = useRef(null);

  const fetchNow = async () => {
    try {
      const r = await fetch(`${API}/api/channel/now`, { cache: 'no-store' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setNowData(j);
      setRemaining(j?.remaining_seconds || 0);
    } catch (_e) {
      // Silent failure — banner just doesn't render. Home has to keep
      // working even if the channel endpoint is down.
      setNowData(null);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    fetchNow();
    // Coarse-grained refresh — server caches the playlist for 5 min so
    // hitting more often than every 60 s wastes cycles.
    fetchTimerRef.current = setInterval(fetchNow, 60_000);
    return () => {
      if (fetchTimerRef.current) clearInterval(fetchTimerRef.current);
    };
  }, []);

  // Fire a banner-impression the first time we've verified there's a live
  // channel to render. Server hourly-dedupes by IP so refreshing Home 20
  // times in an hour still counts as 1 impression. Bots are filtered
  // server-side. See channel_analytics_router.py → banner-event.
  //
  // Guard with `impressionSent` so multiple 60-s /now refreshes within
  // the same page-view don't each fire a new impression fetch. The
  // server-side dedupe would also handle it, but skipping the redundant
  // HTTP call is cheaper.
  const impressionSentRef = useRef(false);
  useEffect(() => {
    if (impressionSentRef.current) return;
    if (!nowData?.live || !nowData?.now?.id) return;
    impressionSentRef.current = true;
    fetch(`${API}/api/channel/banner-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'impression' }),
      keepalive: true,
    }).catch(() => { /* analytics fire-and-forget */ });
  }, [nowData?.live, nowData?.now?.id]);

  // 1-Hz local ticker so the progress bar animates smoothly. When we hit
  // zero, fire an immediate re-fetch instead of waiting for the 60-s
  // interval — this is what makes the banner feel LIVE instead of
  // showing "0s" for up to a minute at every rollover boundary.
  useEffect(() => {
    if (!nowData?.live) return undefined;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          fetchNow();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [nowData?.live, nowData?.now?.id]);

  // Empty-channel guard: renders NOTHING (spec). No skeleton, no error
  // card — Home stays clean if there's nothing to broadcast.
  if (!loaded) return null;
  if (!nowData?.live || !nowData?.now) return null;

  const now = nowData.now;
  const nextTitle = nowData?.next?.[0]?.title;
  const progressPct = Math.max(
    0,
    Math.min(100, ((now.duration - remaining) / Math.max(1, now.duration)) * 100),
  );
  const thumb = resolveMediaUrl(now.thumbnail_url);

  return (
    <button
      onClick={() => {
        // Fire banner-click analytics (server hourly-dedupes) THEN navigate.
        // Using keepalive:true so the fetch survives the navigation. If
        // the network is slow, sendBeacon would be even safer — but the
        // POST body needs the src=banner param on /live so we still want
        // a real fetch. keepalive covers 99% of the "click and immediately
        // navigate" case.
        fetch(`${API}/api/channel/banner-event`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ event: 'click' }),
          keepalive: true,
        }).catch(() => { /* fire-and-forget */ });
        // src=banner is what /live's session/start uses to bucket this
        // viewer as coming from the LiveBanner referrer_source.
        navigate('/live?src=banner');
      }}
      className="w-full mx-auto text-left relative overflow-hidden rounded-2xl border border-red-500/20 bg-zinc-900 group"
      data-testid="live-banner"
      aria-label="Open 24/7 live channel"
    >
      {/* Dimmed thumbnail — pushed behind everything else. `absolute inset-0`
          keeps it behind the content stack; `opacity-40` + a black overlay
          gradient gives the "TV in the background" look without hiding
          the title/artist text. */}
      {thumb && (
        <img
          src={thumb}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-50 transition-opacity"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/40" />

      <div className="relative flex items-center gap-4 p-4">
        {/* Pulsing LIVE dot + label */}
        <div className="flex flex-col items-center gap-1 flex-shrink-0 pr-3 border-r border-white/10">
          <span className="relative flex w-3 h-3">
            <span className="animate-ping absolute inline-flex w-full h-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex w-3 h-3 rounded-full bg-red-500" />
          </span>
          <span className="text-[10px] font-black tracking-widest text-red-400">LIVE</span>
        </div>

        {/* Now playing + up next */}
        <div className="min-w-0 flex-1">
          <div className="text-[10px] text-white/50 uppercase tracking-wide font-semibold">24/7 Channel</div>
          <div className="text-base font-bold text-white truncate" data-testid="live-banner-title">{now.title}</div>
          <div className="text-xs text-white/70 truncate">
            {/* Artist link — MUST use role="link" + tabIndex + keyboard
                handler because the outer banner is a native <button> and
                HTML forbids nested interactive elements. The
                stopPropagation prevents the outer button's onClick
                (navigate to /live) from firing when the artist name is
                tapped. Keyboard: Enter and Space both trigger nav to
                match native button/link behavior on TV remotes. */}
            {now.artist_id ? (
              <span
                role="link"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); navigate(`/creator/${now.artist_id}`); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(`/creator/${now.artist_id}`);
                  }
                }}
                className="hover:text-white hover:underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/60 rounded cursor-pointer"
                data-testid="live-banner-artist-link"
                aria-label={`View artist ${now.artist}`}
              >
                {now.artist}
              </span>
            ) : (
              <span>{now.artist}</span>
            )}
            {nextTitle && (
              <span className="text-white/40">
                {'  ·  Next: '}
                <span className="text-white/60">{nextTitle}</span>
              </span>
            )}
          </div>

          <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-red-500 transition-[width] duration-1000 ease-linear"
              style={{ width: `${progressPct}%` }}
              data-testid="live-banner-progress"
            />
          </div>
          <div className="mt-1 text-[10px] text-white/40 flex items-center justify-between">
            <span>{formatShort(now.duration - remaining)} in</span>
            <span>-{formatShort(remaining)}</span>
          </div>
        </div>

        {/* CTA chip */}
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-500 flex items-center justify-center shadow-lg shadow-red-500/30 group-hover:scale-105 transition-transform">
          <Play className="w-5 h-5 text-white ml-0.5" fill="currentColor" />
        </div>
      </div>
    </button>
  );
};

export default LiveBanner;
